import { useState } from "react";
import { Box, Checkbox, Grid } from "@mui/material";
import { TaskAlt } from "@mui/icons-material";

import CustomText from "../components/CustomText";
import CustomTextField from "../components/CustomTextField";
import CustomButton from "../components/CustomButton";
import HoursDoc from "./HoursDoc";
import { createPreOrder } from "../services/preOrderApi";
import { useFormValidation } from "../hooks/useFormValidation";

const Form = ({ doctor, onSuccess }) => {
  const [terminos, setTerminos] = useState(false);
  const [comunicacion, setComunicacion] = useState(false);

  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [loading, setLoading] = useState(false);
  const { formData, errors, handleChange, resetForm } = useFormValidation({
    nombre: "",
    apellido_paterno: "",
    apellido_materno: "",
    correo_electronico: "",
    telefono: "",
    fecha_nacimiento: "",
    mensaje: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const payload = {
        folio: "",
        medico_uid: doctor?.uid,
        nombre: formData.nombre,
        apellido_paterno: formData.apellido_paterno,
        apellido_materno: formData.apellido_materno,
        correo_electronico: formData.correo_electronico,
        telefono: formData.telefono,
        fecha_nacimiento: formData.fecha_nacimiento,
        mensaje: formData.mensaje,
        fecha_consulta: selectedDate.toISOString().split("T")[0],
      };

      await createPreOrder(payload);

      onSuccess?.();

      resetForm();

      setTerminos(false);
      setComunicacion(false);
    } catch (error) {
      console.error(error);
      alert(error.message || "Ocurrió un error al enviar la solicitud.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={design.Container}>
      {/* Encabezado */}
      <CustomText variant="h3" color="#123055" sx={design.Title}>
        Agenda una consulta
      </CustomText>

      <CustomText variant="body1" color="#6B7280" sx={design.Subtitle}>
        Completa el siguiente formulario y uno de nuestros asesores se pondrá en
        contacto contigo para confirmar la disponibilidad del médico y agendar
        tu cita.
      </CustomText>

      <Grid
        container
        component="form"
        onSubmit={handleSubmit}
        rowGap={3}
        mt={3}
      >
        {/* Nombre */}
        <CustomTextField
          id="nombre"
          name="nombre"
          label="Escribe tu(s) nombre(s)"
          type="text"
          value={formData.nombre}
          onChange={(e) => handleChange("nombre", e.target.value)}
          colorTitle="#123055"
          variant="outlined"
          gridXS={12}
          grid={4}
          required
          sx={design.Grid}
        />

        {/* Apellido paterno */}
        <CustomTextField
          id="apellido_paterno"
          name="apellido_paterno"
          label="Apellido paterno"
          type="text"
          value={formData.apellido_paterno}
          onChange={(e) => handleChange("apellido_paterno", e.target.value)}
          colorTitle="#123055"
          variant="outlined"
          grid={4}
          gridXS={12}
          required
          sx={design.Grid}
        />

        {/* Apellido materno */}
        <CustomTextField
          id="apellido_materno"
          name="apellido_materno"
          label="Apellido materno"
          type="text"
          value={formData.apellido_materno}
          onChange={(e) => handleChange("apellido_materno", e.target.value)}
          colorTitle="#123055"
          variant="outlined"
          grid={4}
          gridXS={12}
          required
          sx={design.Grid}
        />

        {/* Correo */}
        <CustomTextField
          id="correo"
          name="correo"
          label="ejemplo@correo.com"
          type="email"
          value={formData.correo_electronico}
          onChange={(e) => handleChange("correo_electronico", e.target.value)}
          colorTitle="#123055"
          variant="outlined"
          grid={12}
          gridXS={12}
          sx={design.Grid}
          width="99%"
          required
          error={!!errors.correo_electronico}
          helperText={errors.correo_electronico}
        />

        {/* Teléfono */}
        <CustomTextField
          id="telefono"
          name="telefono"
          label="Número telefónico"
          type="tel"
          value={formData.telefono}
          onChange={(e) => handleChange("telefono", e.target.value)}
          colorTitle="#123055"
          variant="outlined"
          grid={6}
          gridXS={12}
          sx={design.Grid}
          error={!!errors.telefono}
          helperText={errors.telefono}
          required
          inputProps={{
            maxLength: 10,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
        />

        {/* Fecha de nacimiento */}
        <CustomTextField
          id="fechaNacimiento"
          name="fechaNacimiento"
          type="date"
          value={formData.fecha_nacimiento}
          onChange={(e) => handleChange("fecha_nacimiento", e.target.value)}
          colorTitle="#123055"
          variant="outlined"
          grid={6}
          gridXS={12}
          required
          sx={design.Grid}
        />

        {/* Selector de fecha */}
        <HoursDoc
          doctor={doctor}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
        />

        {/* Mensaje */}
        <CustomTextField
          id="mensaje"
          name="mensaje"
          label="Escribe indicaciones especiales o información importante para el médico..."
          type="text"
          multiline
          rows={3}
          value={formData.mensaje}
          onChange={(e) => handleChange("mensaje", e.target.value)}
          colorTitle="#123055"
          variant="outlined"
          grid={12}
          gridXS={12}
        />

        {/* Términos */}
        <Grid size={{ xs: 12 }}>
          <Box sx={design.CheckContainer}>
            <Checkbox
              checked={terminos}
              onChange={(e) => setTerminos(e.target.checked)}
              sx={design.Checkbox}
            />

            <CustomText variant="body2" color="#4B5563" sx={design.CheckText}>
              He leído y acepto el{" "}
              <a href="/aviso-de-privacidad" style={design.Link}>
                Aviso de Privacidad
              </a>{" "}
              y los{" "}
              <a href="/terminos-de-uso" style={design.Link}>
                Términos y Condiciones
              </a>
              .
            </CustomText>
          </Box>
        </Grid>

        {/* Comunicación */}
        <Grid size={{ xs: 12 }}>
          <Box sx={design.CheckContainer}>
            <Checkbox
              checked={comunicacion}
              onChange={(e) => setComunicacion(e.target.checked)}
              sx={design.Checkbox}
            />

            <CustomText variant="body2" color="#4B5563" sx={design.CheckText}>
              Acepto recibir información, promociones y noticias del Centro
              Médico Medam.
            </CustomText>
          </Box>
        </Grid>

        {/* Botón */}
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CustomButton
            type="submit"
            variant="contained"
            backgroundColor="#2D9BB3"
            colorTexto="#FFFFFF"
            width="100%"
            height={58}
            borderRadius={16}
            disabled={loading}
            style={design.Button}
          >
            {loading ? "Enviando solicitud..." : "Solicitar cita ahora"}

            {!loading && (
              <TaskAlt
                sx={{
                  ml: 1,
                  fontSize: 22,
                }}
              />
            )}
          </CustomButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Form;

const design = {
  Container: {
    height: "100%",
    padding: {
      xs: "35px 24px",
      md: "20px 35px",
    },
    background: "#FFFFFF",
  },

  Title: {
    fontFamily: "Plus Jakarta Sans",
    fontWeight: 800,
    fontSize: {
      xs: 24,
      md: 26,
    },
    lineHeight: 1.1,
    marginBottom: 1,
  },

  Subtitle: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: 15,
    maxWidth: 650,
  },

  Grid: {
    marginBottom: -10,
  },

  CheckContainer: {
    display: "flex",
    alignItems: "center",
  },

  Checkbox: {
    color: "#2D9BB3",
    "&.Mui-checked": {
      color: "#2D9BB3",
    },
  },

  CheckText: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: 12,
    lineHeight: 1.7,
  },

  Link: {
    color: "#2D9BB3",
    textDecoration: "none",
    fontWeight: 700,
  },

  Button: {
    marginTop: 10,
    fontFamily: "Plus Jakarta Sans",
    fontSize: 13,
    fontWeight: 700,
    boxShadow: "0px 10px 30px rgba(45,155,179,.35)",
    transition: ".3s",

    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0px 14px 35px rgba(45,155,179,.45)",
    },
  },
};
